import { useTranslation } from "react-i18next";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import { FieldDef } from "../typeConfig";
import FieldCard from "./FieldCard";
import FieldCardSkeleton from "./FieldCardSkeleton";
import { CurrentTypeState } from "./useTemplateForm";

type Props = {
  isEdit: boolean;
  isLoadingEdit: boolean;
  submitted: boolean;
  typeState: CurrentTypeState;
};

export default function TypeTabContent({
  isEdit,
  isLoadingEdit,
  submitted,
  typeState,
}: Props) {
  const { t } = useTranslation("storage");
  const classes = useStyles();
  const {
    config,
    active,
    data,
    savedData,
    defaults,
    loadingDefaults,
    toggle,
    setFieldState,
    setFieldValue,
    setToggleValue,
  } = typeState;

  const requiredFields = config.fields.filter(
    f => f.kind !== "layout" && !f.sectionKey
  );
  const optionalFields = config.fields.filter(f => !!f.sectionKey);
  const layoutFields = config.fields.filter(f => f.kind === "layout");

  if (isLoadingEdit) {
    return <FieldCardSkeleton fields={config.fields} />;
  }

  return (
    <>
      <SwitchField
        name={`type-${config.formKey}`}
        label={t(`notificationTemplates.type.${config.formKey}.label`)}
        description={t(
          `notificationTemplates.type.${config.formKey}.description`
        )}
        value={active}
        onChange={toggle}
      />

      {!active && (
        <div className={classes.infoBoxWrapper}>
          <div className={classes.infoBox}>
            <Typography variant="body2" color="textSecondary">
              {t("notificationTemplates.form.infoBox")}
            </Typography>
          </div>
        </div>
      )}

      {active && loadingDefaults && (
        <FieldCardSkeleton fields={config.fields} />
      )}

      {active && !loadingDefaults && (
        <>
          {requiredFields.length > 0 && (
            <FieldSection
              title={t("notificationTemplates.form.sectionRequired")}
              fields={requiredFields}
              typeData={data}
              savedTypeData={savedData}
              typeDefaults={defaults}
              isEdit={isEdit}
              submitted={submitted}
              onFieldStateChange={setFieldState}
              onFieldChange={setFieldValue}
            />
          )}
          {optionalFields.length > 0 && (
            <FieldSection
              title={t("notificationTemplates.form.sectionOptional")}
              fields={optionalFields}
              typeData={data}
              savedTypeData={savedData}
              typeDefaults={defaults}
              isEdit={isEdit}
              submitted={submitted}
              onFieldStateChange={setFieldState}
              onFieldChange={setFieldValue}
            />
          )}
          {layoutFields.length > 0 && (
            <FieldSection
              title={t("notificationTemplates.form.sectionLayout")}
              fields={layoutFields}
              typeData={data}
              savedTypeData={savedData}
              typeDefaults={defaults}
              isEdit={isEdit}
              submitted={submitted}
              onFieldStateChange={setFieldState}
              onFieldChange={setFieldValue}
            />
          )}
          {config.toggles?.map(toggle => {
            const val = data?.[toggle.key];
            const defaultVal = defaults?.sections?.[toggle.key] ?? true;
            const checked =
              val === false ? false : val === true ? true : defaultVal;
            return (
              <SwitchField
                key={toggle.key}
                name={toggle.key}
                label={t(`notificationTemplates.form.toggle.${toggle.key}`)}
                value={checked}
                onChange={() => setToggleValue(toggle.key, !checked)}
                style={{ marginTop: 16 }}
              />
            );
          })}
        </>
      )}
    </>
  );
}

type FieldSectionProps = {
  title: string;
  fields: FieldDef[];
  typeData: CurrentTypeState["data"];
  savedTypeData: CurrentTypeState["savedData"];
  typeDefaults: CurrentTypeState["defaults"];
  isEdit: boolean;
  submitted: boolean;
  onFieldStateChange: CurrentTypeState["setFieldState"];
  onFieldChange: CurrentTypeState["setFieldValue"];
};

function FieldSection({
  title,
  fields,
  typeData,
  savedTypeData,
  typeDefaults,
  isEdit,
  submitted,
  onFieldStateChange,
  onFieldChange,
}: FieldSectionProps) {
  const classes = useStyles();

  return (
    <div className={classes.section}>
      <Typography variant="overline" color="textSecondary">
        {title}
      </Typography>
      <Grid container spacing={2}>
        {fields.map(field => {
          const currentVal = typeData?.[field.key];
          const savedVal = savedTypeData?.[field.key];

          return (
            <Grid item xs={12} md={6} key={field.key}>
              <FieldCard
                field={field}
                currentVal={currentVal}
                defaultVal={typeDefaults?.texts?.[field.key] || ""}
                savedVal={typeof savedVal === "string" ? savedVal : undefined}
                isEdit={isEdit}
                submitted={submitted}
                onStateChange={state => onFieldStateChange(field, state)}
                onChange={v => onFieldChange(field.key, v)}
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}

const useStyles = makeStyles(() => ({
  section: {
    marginTop: 20,
  },
  infoBoxWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 220,
  },
  infoBox: {
    textAlign: "center" as const,
    padding: "32px 24px",
    maxWidth: "70%",
    backgroundColor: "rgb(238, 242, 246)",
    border: "2px dashed rgba(0, 0, 0, 0.30)",
    borderRadius: 10,
  },
}));
